import Link from "next/link";
import { ShortUser } from "@/api/users/types";
import { twMerge } from "tailwind-merge";
import { UserImage } from "@/app/(components)/Basic/UserImage";
import { Button } from "@/app/(components)/Basic/Button";
import { useMemo, useState } from "react";
import { ART_ORDER_DEFAULT_PAGE, ArtOrderStatus } from "../constants";
import { ArtOrderFull, ArtOrderOffer } from "@/api/art-order/types";
import TrashIcon from "@/assets/icons/trash.svg";
import EditIcon from "@/assets/icons/edit.svg";
import ClockIcon from "@/assets/icons/clock.svg";
import { ExtendOrderModal } from "../ExtendOrderModal";
import { CancelArtOrderButton } from "../ArtOrderActions/CancelOrder/CancelArtOrderButton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import artOrderApi from "@/api/art-order/order/client";
import { useRouter } from "next/navigation";
import { getArtOrderEditRouteById } from "../helpers";
import { ConfirmModal } from "@/app/(components)/Basic/ConfirmModal";
import { UserLink } from "../../User/UserLink";
import { getUsersName } from "@/api/users/helpers";

export interface Props {
  user: ShortUser;
  order?: ArtOrderFull;
  className?: string;
  label?: string;
  labelClassName?: string;
  isUserAuthor?: boolean;
  orderExceeds?: boolean;
}

const ArtOrderUser = ({
  user,
  order,
  className,
  label,
  labelClassName,
  isUserAuthor,
  orderExceeds,
}: Props) => {
  const [isExtendOrderModalOpen, setIsExtendOrderModalOpen] =
    useState<boolean>(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const router = useRouter();

  const queryClient = useQueryClient();

  const showEditButton = useMemo(
    () =>
      order?.status === ArtOrderStatus.ACCEPTING_OFFERS &&
      !order.offers?.length,
    [order?.status, order?.offers],
  );

  const showDeleteButton = useMemo(
    () => order?.status === ArtOrderStatus.ACCEPTING_OFFERS,
    [order?.status],
  );

  const { mutateAsync: deleteOrder, isLoading } = useMutation({
    mutationFn: artOrderApi.deleteArtOrder,
    onSuccess: () => {
      queryClient.invalidateQueries(["my-art-orders", ART_ORDER_DEFAULT_PAGE]);
      queryClient.invalidateQueries([
        "all-active-art-orders",
        ART_ORDER_DEFAULT_PAGE,
      ]);
      queryClient.invalidateQueries([
        "my-art-orders-count",
        ART_ORDER_DEFAULT_PAGE,
      ]);
      queryClient.invalidateQueries([
        "all-active-art-orders-count",
        ART_ORDER_DEFAULT_PAGE,
      ]);
    },
  });

  const onOrderExtend = () => {
    setIsExtendOrderModalOpen(false);
  };

  const onDeleteOrderClick = () => {
    setIsDeleteModalOpen(true);
  };

  const onDeleteOrder = async () => {
    await deleteOrder(order!.id!);

    router.push("/art-orders/my");
  };

  return (
    <>
      {label && <span className={labelClassName}>{label}</span>}
      <div className={twMerge("flex lg:flex-col lg:items-center", className)}>
        <UserLink artist={user}>
          <UserImage
            image={{ src: user.imageUrl }}
            imagePlaceholder={user.imagePlaceholder}
            className="w-[60px] h-[60px] lg:w-[80px] lg:h-[80px] mr-6 lg:mr-0"
          />
        </UserLink>
        <div className="lg:flex lg:flex-col items-center lg:text-center">
          <UserLink
            artist={user}
            className={`text-sm endemic:text-primary ${user.hideProfile ? "text-primary" : "text-red"}`}
          >
            <div className="text-lg leading-5 mt-2 lg:mt-4 truncate lg:w-40 text-primary">
              {getUsersName(user, true)}
            </div>
            {user.displayName ? `@${user.displayName}` : "unnamed"}
          </UserLink>
          {isUserAuthor && (
            <span className="mt-6 flex flex-col gap-y-5">
              {showEditButton && (
                <Link href={getArtOrderEditRouteById(order!.id)}>
                  <Button
                    startIcon={<EditIcon className="w-5 h-5 mr-2" />}
                    className="w-[180px]"
                  >
                    Edit order
                  </Button>
                </Link>
              )}
              {orderExceeds && (
                <>
                  <Button
                    onClick={() => setIsExtendOrderModalOpen(true)}
                    className="w-[180px]"
                    startIcon={
                      <ClockIcon className="w-5 h-5 fill-primary endemic:fill-invert mr-2" />
                    }
                  >
                    Extend order
                  </Button>
                  <CancelArtOrderButton
                    artOrderOffer={
                      {
                        ...order!.acceptedOffer,
                        order: order!,
                      } as ArtOrderOffer
                    }
                  />
                </>
              )}
              {showDeleteButton && (
                <Button
                  startIcon={
                    <TrashIcon className="w-5 h-5 fill-primary endemic:fill-invert" />
                  }
                  onClick={onDeleteOrderClick}
                >
                  Delete order
                </Button>
              )}
            </span>
          )}
        </div>
      </div>

      {orderExceeds && (
        <ExtendOrderModal
          isOpen={isExtendOrderModalOpen}
          handleClose={() => setIsExtendOrderModalOpen(false)}
          onSuccess={() => onOrderExtend()}
          artOrder={order!}
          acceptedOffer={order!.acceptedOffer!}
        />
      )}

      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onSubmit={onDeleteOrder}
          isLoading={isLoading}
          text="Are you sure you want to delete this order?"
          title="Delete order"
        />
      )}
    </>
  );
};

export default ArtOrderUser;
